import axios, { CancelToken } from 'axios';
import Config from '../../../config';
import Fuse from 'fuse.js';

const AI_TIMEOUT = 30000;
const MAX_BATCH_SIZE = 50;

const CONFIDENCE = {
    EXACT_MATCH: 1.0,
    HIGH: 0.85,
    FUZZY_MATCH: 0.7,
    MODERATE: 0.65,
    DEFAULT: 0.5
};

export interface AiConversationMessage {
    role?: string;
    content?: string;
    [key: string]: unknown;
}

export interface AiAttributeMetadata {
    Type?: string;
    Editor?: string;
    Options?: string[];
    [key: string]: unknown;
}

export interface AiMappingSuggestion {
    suggested: string;
    confidence: number;
    source: 'AI';
}

export interface AiMapAttributesResult {
    mappings: Record<string, AiMappingSuggestion>;
    errors: string[];
}

export interface AiEnrichAttributeResult {
    success: boolean;
    value: string | string[];
    originalSuggestion?: string | string[];
    messages: AiConversationMessage[];
    sessionId: string | null;
    error?: string;
}

export interface AiProductScoreSuggestion {
    Field?: string;
    Issue?: string;
    Recommendation?: string;
    [key: string]: unknown;
}

export interface AiProductScoreResult {
    success: boolean;
    score: number;
    suggestions: AiProductScoreSuggestion[];
    error?: string;
}

type ProductAttributes = Record<string, unknown>;

const cleanProductAttributes = (attributes: ProductAttributes): ProductAttributes =>
    Object.entries(attributes || {}).reduce<ProductAttributes>((obj, [key, value]) => {
        if (key !== 'tags' && !key.startsWith('_') && !Array.isArray(value)) {
            obj[key] = value;
        }
        return obj;
    }, {});

export const aiMapAttributes = async (inputValues: string[], availableValues: string[]): Promise<AiMapAttributesResult> => {
    try {
        if (!inputValues || !Array.isArray(inputValues) || inputValues.length === 0) {
            throw new Error('Invalid input values for AI mapping');
        }

        if (!availableValues || !Array.isArray(availableValues) || availableValues.length === 0) {
            throw new Error('Invalid available values for AI mapping');
        }

        let processedInputValues = inputValues;
        if (processedInputValues.length > MAX_BATCH_SIZE) {
            console.warn(`AI mapping batch size (${processedInputValues.length}) exceeds maximum (${MAX_BATCH_SIZE}). Processing first ${MAX_BATCH_SIZE} items.`);
            processedInputValues = processedInputValues.slice(0, MAX_BATCH_SIZE);
        }

        const request = {
            InputValues: processedInputValues,
            AvailableValues: availableValues
        };

        const response = await axios.post(
            `${Config.api}/odata/Company/Functions.AiMapListValues`,
            request,
            { timeout: AI_TIMEOUT }
        );

        if (response.data) {
            return parseAIMappingResponse(response.data, processedInputValues);
        }

        throw new Error('Empty response from AI mapping service');
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'AI mapping service unavailable';
        console.error('AI mapping service error:', error);

        return {
            mappings: {},
            errors: [errorMessage]
        };
    }
};

const parseAIMappingResponse = (response: unknown, inputValues: string[]): AiMapAttributesResult => {
    const mappings: Record<string, AiMappingSuggestion> = {};
    const errors: string[] = [];

    try {
        const responseObj = response as { value?: Array<{ InputValue?: string; MappedValue?: string; Confidence?: number }> };
        const mappingData = responseObj.value || (response as Array<{ InputValue?: string; MappedValue?: string; Confidence?: number }>);

        if (!Array.isArray(mappingData)) {
            throw new Error('Invalid AI mapping response format');
        }

        mappingData.forEach(mapping => {
            if (mapping.InputValue && mapping.MappedValue) {
                mappings[mapping.InputValue] = {
                    suggested: mapping.MappedValue,
                    confidence: mapping.Confidence || CONFIDENCE.DEFAULT,
                    source: 'AI'
                };
            }
        });

        inputValues.forEach(input => {
            if (!mappings[input]) {
                errors.push(`No AI mapping found for: ${input}`);
            }
        });
    } catch (error) {
        console.error('Error parsing AI response:', error);
        errors.push('Failed to parse AI mapping response');
    }

    return { mappings, errors };
};

const normalize = (str: string): string => {
    return str.toLowerCase().trim().replace(/[-_]/g, ' ').replace(/\s+/g, ' ');
};

const findBestMatchingOption = (suggestion: string, availableOptions: string[]): string | null => {
    if (!suggestion || !availableOptions || availableOptions.length === 0) {
        return null;
    }

    const normalizedSuggestion = normalize(suggestion);
    const exactMatch = availableOptions.find(option => normalize(option) === normalizedSuggestion);
    if (exactMatch) return exactMatch;

    const fuse = new Fuse(availableOptions, {
        includeScore: true,
        threshold: 0.3,
        ignoreLocation: true,
        minMatchCharLength: 2
    });

    const results = fuse.search(suggestion);
    return results.length > 0 ? results[0].item : null;
};

export const aiEnrichAttribute = async (
    attributeName: string,
    attributes: ProductAttributes,
    messages: AiConversationMessage[] = [],
    sessionId: string | null = null,
    attributeMetadata: AiAttributeMetadata = {},
    feedback: string | null = null
): Promise<AiEnrichAttributeResult> => {
    try {
        const cleanAttributes = cleanProductAttributes(attributes);

        const request: {
            AttributeName: string;
            Attributes: ProductAttributes;
            SessionId?: string;
            Messages?: AiConversationMessage[];
            Feedback?: string;
            AvailableOptions?: string[];
            AttributeType?: string;
            Editor?: string;
        } = {
            AttributeName: attributeName,
            Attributes: cleanAttributes
        };

        if (sessionId) request.SessionId = sessionId;
        if (messages && messages.length > 0) request.Messages = messages;
        if (feedback && feedback.trim()) request.Feedback = feedback;
        if (attributeMetadata.Editor) request.Editor = attributeMetadata.Editor;
        if (attributeMetadata.Options && attributeMetadata.Options.length > 0) {
            request.AvailableOptions = attributeMetadata.Options;
            request.AttributeType = attributeMetadata.Type;
        }

        const response = await axios.post(
            `${Config.api}/odata/Company/Functions.AiEnrichValue`,
            request,
            { timeout: AI_TIMEOUT }
        );

        if (response.data) {
            const result = (response.data.value || response.data) as {
                Suggestion?: string | string[];
                SessionId?: string;
                Messages?: AiConversationMessage[];
            };

            let enrichedValue: string | string[] = result.Suggestion || '';

            if (attributeMetadata.Type === 'select' && attributeMetadata.Options && typeof enrichedValue === 'string') {
                const matchedOption = findBestMatchingOption(enrichedValue, attributeMetadata.Options);
                if (matchedOption) {
                    enrichedValue = matchedOption;
                } else {
                    console.warn(`Could not match AI suggestion "${enrichedValue}" to available options`);
                }
            }

            if (attributeMetadata.Type === 'list' && attributeMetadata.Options) {
                const suggestions = Array.isArray(enrichedValue)
                    ? enrichedValue
                    : String(enrichedValue).split(',').map(s => s.trim());

                enrichedValue = suggestions.map(suggestion => {
                    const matched = findBestMatchingOption(suggestion, attributeMetadata.Options || []);
                    return matched || suggestion;
                });
            }

            return {
                success: true,
                value: enrichedValue,
                originalSuggestion: result.Suggestion,
                messages: result.Messages || messages,
                sessionId: result.SessionId || sessionId
            };
        }

        throw new Error('Empty response from AI enrichment service');
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'AI enrichment service unavailable';
        console.error('AI enrichment error:', error);
        return {
            success: false,
            error: errorMessage,
            value: '',
            messages,
            sessionId
        };
    }
};

export const aiProductScore = async (
    attributes: ProductAttributes,
    availableAttributes: Array<{ Name: string }> = [],
    cancelToken?: CancelToken,
    retailerCoid?: number
): Promise<AiProductScoreResult> => {
    try {
        let fullAttributes = attributes;
        if (availableAttributes && availableAttributes.length > 0) {
            fullAttributes = {};
            availableAttributes.forEach(attr => {
                fullAttributes[attr.Name] = attributes[attr.Name] ?? '';
            });
        }

        const request: { Attributes: ProductAttributes; ChannelCoid?: number } = {
            Attributes: cleanProductAttributes(fullAttributes)
        };
        if (retailerCoid) request.ChannelCoid = retailerCoid;

        const config: { timeout: number; cancelToken?: CancelToken } = { timeout: AI_TIMEOUT };
        if (cancelToken) config.cancelToken = cancelToken;

        const response = await axios.post(`${Config.api}/odata/Company/Functions.AiProductScore`, request, config);

        if (response.data) {
            const result = (response.data.value || response.data) as {
                TotalScore?: number;
                Suggestions?: AiProductScoreSuggestion[];
            };
            return {
                success: true,
                score: result.TotalScore || 0,
                suggestions: result.Suggestions || []
            };
        }

        throw new Error('Empty response from AI scoring service');
    } catch (error) {
        if (axios.isCancel(error)) {
            throw error;
        }
        const errorMessage = error instanceof Error ? error.message : 'AI scoring service unavailable';
        console.error('AI product score error:', error);
        return {
            success: false,
            error: errorMessage,
            score: 0,
            suggestions: []
        };
    }
};

const aiService = {
    aiMapAttributes,
    aiEnrichAttribute,
    aiProductScore
};

export default aiService;
